import { createReducer, on } from '@ngrx/store';
import * as CrmActions from './crm.actions';

export interface CrmState {
  // Contacts
  contacts: any[];
  contactsLoading: boolean;
  contactsError: any;
  contactsPagination: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
  };
  contactDetail: any;
  contactDetailLoading: boolean;
  contactDetailError: any;
  contactsLookup: any[];
  contactsLookupLoading: boolean;

  // Companies
  companies: any[];
  companiesLoading: boolean;
  companiesError: any;
  companiesPagination: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
  };
  companyDetail: any;
  companyDetailLoading: boolean;
  companyDetailError: any;
  companiesLookup: any[];
  companiesLookupLoading: boolean;

  // Deals
  deals: any[];
  dealsLoading: boolean;
  dealsError: any;
  dealsPagination: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
  };
  dealDetail: any;
  dealDetailLoading: boolean;
  dealDetailError: any;

  // Operation results
  operationResult: any;
  operationLoading: boolean;
  operationError: any;

  // Save Report to CRM
  saveReportLoading: boolean;
  saveReportResult: any;
  saveReportError: any;
}

export const initialState: CrmState = {
  // Contacts
  contacts: [],
  contactsLoading: false,
  contactsError: null,
  contactsPagination: {
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 0,
    to: 0
  },
  contactDetail: null,
  contactDetailLoading: false,
  contactDetailError: null,
  contactsLookup: [],
  contactsLookupLoading: false,

  // Companies
  companies: [],
  companiesLoading: false,
  companiesError: null,
  companiesPagination: {
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 0,
    to: 0
  },
  companyDetail: null,
  companyDetailLoading: false,
  companyDetailError: null,
  companiesLookup: [],
  companiesLookupLoading: false,

  // Deals
  deals: [],
  dealsLoading: false,
  dealsError: null,
  dealsPagination: {
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 0,
    to: 0
  },
  dealDetail: null,
  dealDetailLoading: false,
  dealDetailError: null,

  // Operation results
  operationResult: null,
  operationLoading: false,
  operationError: null,

  // Save Report to CRM
  saveReportLoading: false,
  saveReportResult: null,
  saveReportError: null
};

export const crmReducer = createReducer(
  initialState,

  // Contact Reducers
  on(CrmActions.loadContacts, (state) => ({
    ...state,
    // Clear the list synchronously so the table can't render stale rows
    // while the new request is in flight — keeps the spinner the only
    // thing visible across all CRM tabs.
    contacts: [],
    contactsLoading: true,
    contactsError: null
  })),

  on(CrmActions.loadContactsSuccess, (state, { result, pagination }) => {
    // console.log('loadContactsSuccess - result:', result);
    // console.log('loadContactsSuccess - pagination:', pagination);
    return {
      ...state,
      contacts: result || [],
      contactsPagination: pagination || state.contactsPagination,
      contactsLoading: false,
      contactsError: null
    };
  }),

  on(CrmActions.loadContactsFailure, (state, { error }) => ({
    ...state,
    contactsLoading: false,
    contactsError: error
  })),

  on(CrmActions.createContact, (state) => ({
    ...state,
    operationLoading: true,
    operationError: null
  })),

  on(CrmActions.createContactSuccess, (state, { result }) => ({
    ...state,
    operationLoading: false,
    operationResult: result,
    operationError: null
  })),

  on(CrmActions.createContactFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error
  })),

  on(CrmActions.updateContact, (state) => ({
    ...state,
    operationLoading: true,
    operationError: null
  })),

  on(CrmActions.updateContactSuccess, (state, { result }) => ({
    ...state,
    contacts: state.contacts.map(contact =>
      contact.id === result.id ? result : contact
    ),
    operationLoading: false,
    operationResult: result,
    operationError: null
  })),

  on(CrmActions.updateContactFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error
  })),

  on(CrmActions.deleteContact, (state) => ({
    ...state,
    operationLoading: true,
    operationError: null
  })),

  on(CrmActions.deleteContactSuccess, (state, { id }) => ({
    ...state,
    contacts: state.contacts.filter(contact => contact.id !== id),
    operationLoading: false,
    operationError: null,
    contactsPagination: {
      ...state.contactsPagination,
      total: state.contactsPagination.total - 1
    }
  })),

  on(CrmActions.deleteContactFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error
  })),

  on(CrmActions.loadContactDetail, (state) => ({
    ...state,
    contactDetailLoading: true,
    contactDetailError: null
  })),

  on(CrmActions.loadContactDetailSuccess, (state, { result }) => ({
    ...state,
    contactDetail: result,
    contactDetailLoading: false,
    contactDetailError: null
  })),

  on(CrmActions.loadContactDetailFailure, (state, { error }) => ({
    ...state,
    contactDetail: null,
    contactDetailLoading: false,
    contactDetailError: error
  })),

  on(CrmActions.loadContactsLookup, (state) => ({
    ...state,
    contactsLookupLoading: true
  })),

  on(CrmActions.loadContactsLookupSuccess, (state, { result }) => ({
    ...state,
    contactsLookup: result || [],
    contactsLookupLoading: false
  })),

  on(CrmActions.loadContactsLookupFailure, (state) => ({
    ...state,
    contactsLookupLoading: false
  })),

  // Company Reducers
  on(CrmActions.loadCompanies, (state) => ({
    ...state,
    // Clear synchronously so the table renders no stale rows during refetch.
    companies: [],
    companiesLoading: true,
    companiesError: null
  })),

  on(CrmActions.loadCompaniesSuccess, (state, { result, pagination }) => ({
    ...state,
    companies: result,
    companiesPagination: pagination,
    companiesLoading: false,
    companiesError: null
  })),

  on(CrmActions.loadCompaniesFailure, (state, { error }) => ({
    ...state,
    companiesLoading: false,
    companiesError: error
  })),

  on(CrmActions.createCompany, (state) => ({
    ...state,
    operationLoading: true,
    operationError: null
  })),

  on(CrmActions.createCompanySuccess, (state, { result }) => ({
    ...state,
    operationLoading: false,
    operationResult: result,
    operationError: null
  })),

  on(CrmActions.createCompanyFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error
  })),

  on(CrmActions.updateCompany, (state) => ({
    ...state,
    operationLoading: true,
    operationError: null
  })),

  on(CrmActions.updateCompanySuccess, (state, { result }) => ({
    ...state,
    companies: state.companies.map(company =>
      company.id === result.id ? result : company
    ),
    operationLoading: false,
    operationResult: result,
    operationError: null
  })),

  on(CrmActions.updateCompanyFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error
  })),

  on(CrmActions.deleteCompany, (state) => ({
    ...state,
    operationLoading: true,
    operationError: null
  })),

  on(CrmActions.deleteCompanySuccess, (state, { id }) => ({
    ...state,
    companies: state.companies.filter(company => company.id !== id),
    operationLoading: false,
    operationError: null,
    companiesPagination: {
      ...state.companiesPagination,
      total: state.companiesPagination.total - 1
    }
  })),

  on(CrmActions.deleteCompanyFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error
  })),

  on(CrmActions.loadCompanyDetail, (state) => ({
    ...state,
    companyDetailLoading: true,
    companyDetailError: null
  })),

  on(CrmActions.loadCompanyDetailSuccess, (state, { result }) => ({
    ...state,
    companyDetail: result,
    companyDetailLoading: false,
    companyDetailError: null
  })),

  on(CrmActions.loadCompanyDetailFailure, (state, { error }) => ({
    ...state,
    companyDetail: null,
    companyDetailLoading: false,
    companyDetailError: error
  })),

  on(CrmActions.loadCompaniesLookup, (state) => ({
    ...state,
    companiesLookupLoading: true
  })),

  on(CrmActions.loadCompaniesLookupSuccess, (state, { result }) => ({
    ...state,
    companiesLookup: result || [],
    companiesLookupLoading: false
  })),

  on(CrmActions.loadCompaniesLookupFailure, (state) => ({
    ...state,
    companiesLookupLoading: false
  })),

  // Deal Reducers
  on(CrmActions.loadDeals, (state) => ({
    ...state,
    // Clear synchronously so the table renders no stale rows during refetch.
    deals: [],
    dealsLoading: true,
    dealsError: null
  })),

  on(CrmActions.loadDealsSuccess, (state, { result, pagination }) => ({
    ...state,
    deals: result,
    dealsPagination: pagination,
    dealsLoading: false,
    dealsError: null
  })),

  on(CrmActions.loadDealsFailure, (state, { error }) => ({
    ...state,
    dealsLoading: false,
    dealsError: error
  })),

  on(CrmActions.createDeal, (state) => ({
    ...state,
    operationLoading: true,
    operationError: null
  })),

  on(CrmActions.createDealSuccess, (state, { result }) => ({
    ...state,
    operationLoading: false,
    operationResult: result,
    operationError: null
  })),

  on(CrmActions.createDealFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error
  })),

  on(CrmActions.updateDeal, (state) => ({
    ...state,
    operationLoading: true,
    operationError: null
  })),

  on(CrmActions.updateDealSuccess, (state, { result }) => ({
    ...state,
    deals: state.deals.map(deal =>
      deal.id === result.id ? result : deal
    ),
    operationLoading: false,
    operationResult: result,
    operationError: null
  })),

  on(CrmActions.updateDealFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error
  })),

  on(CrmActions.deleteDeal, (state) => ({
    ...state,
    operationLoading: true,
    operationError: null
  })),

  on(CrmActions.deleteDealSuccess, (state, { id }) => ({
    ...state,
    deals: state.deals.filter(deal => deal.id !== id),
    operationLoading: false,
    operationError: null,
    dealsPagination: {
      ...state.dealsPagination,
      total: state.dealsPagination.total - 1
    }
  })),

  on(CrmActions.deleteDealFailure, (state, { error }) => ({
    ...state,
    operationLoading: false,
    operationError: error
  })),

  on(CrmActions.loadDealDetail, (state) => ({
    ...state,
    dealDetailLoading: true,
    dealDetailError: null
  })),

  on(CrmActions.loadDealDetailSuccess, (state, { result }) => ({
    ...state,
    dealDetail: result,
    dealDetailLoading: false,
    dealDetailError: null
  })),

  on(CrmActions.loadDealDetailFailure, (state, { error }) => ({
    ...state,
    dealDetail: null,
    dealDetailLoading: false,
    dealDetailError: error
  })),

  // Save Report to CRM Reducers
  on(CrmActions.saveReportToCrm, (state) => ({
    ...state,
    saveReportLoading: true,
    saveReportError: null
  })),

  on(CrmActions.saveReportToCrmSuccess, (state, { result }) => ({
    ...state,
    saveReportLoading: false,
    saveReportResult: result,
    saveReportError: null
  })),

  on(CrmActions.saveReportToCrmFailure, (state, { error }) => ({
    ...state,
    saveReportLoading: false,
    saveReportError: error
  }))
);
